import { useMutation, UseMutationOptions, useQuery, useQueryClient } from "react-query";
import { useRecoilValue } from "recoil";
import { toast } from "src/components";
import { authState } from "src/recoils";
import {
  BlacklistItem,
  CreateBlacklistBankAccount,
  CreateBlacklistBankAccountRequest,
  CreateBlacklistPhone,
  CreateBlacklistPhoneRequest,
  GetBlacklistBankAccounts,
  GetBlacklistPhones,
} from "src/services/blacklist";

const BLACKLIST_PHONES_QUERY_KEY = "blacklistPhones";
const BLACKLIST_BANK_ACCOUNTS_QUERY_KEY = "blacklistBankAccounts";

export type BlacklistListQuery = { page: number; perPage: number; search: string };

export type BlacklistPage = { results: BlacklistItem[]; total: number };

const toBlacklistPage = (data?: { results?: BlacklistItem[]; total?: number }): BlacklistPage => {
  const results = data?.results || [];
  return { results, total: data?.total ?? results.length };
};

export function useBlacklistPhones({ page, perPage, search }: BlacklistListQuery, enabled = true) {
  const { token = "" } = useRecoilValue(authState);
  return useQuery(
    [BLACKLIST_PHONES_QUERY_KEY, "phone", page, perPage, search],
    async () => {
      const response = await GetBlacklistPhones({ token, page, per_page: perPage, search });
      return toBlacklistPage(response.data);
    },
    {
      keepPreviousData: true,
      enabled,
      onError: () => toast.error("โหลดรายการเบอร์โทรแบล็คลิสไม่สำเร็จ"),
    }
  );
}

export function useCreateBlacklistPhone(
  options?: UseMutationOptions<unknown, unknown, Omit<CreateBlacklistPhoneRequest, "token">, unknown>
) {
  const { token = "" } = useRecoilValue(authState);
  const queryClient = useQueryClient();
  return useMutation((request) => CreateBlacklistPhone({ token, ...request }), {
    onError: () => toast.error("เพิ่มเบอร์โทรแบล็คลิสไม่สำเร็จ"),
    onSuccess: () => {
      toast.success("เพิ่มเบอร์โทรแบล็คลิสสำเร็จ");
      queryClient.invalidateQueries([BLACKLIST_PHONES_QUERY_KEY]);
    },
    ...options,
  });
}

export function useBlacklistBankAccounts({ page, perPage, search }: BlacklistListQuery, enabled = true) {
  const { token = "" } = useRecoilValue(authState);
  return useQuery(
    [BLACKLIST_BANK_ACCOUNTS_QUERY_KEY, "bankAccount", page, perPage, search],
    async () => {
      const response = await GetBlacklistBankAccounts({ token, page, per_page: perPage, search });
      return toBlacklistPage(response.data);
    },
    {
      keepPreviousData: true,
      enabled,
      onError: () => toast.error("โหลดรายการชื่อบัญชีแบล็คลิสไม่สำเร็จ"),
    }
  );
}

export function useCreateBlacklistBankAccount(
  options?: UseMutationOptions<
    unknown,
    unknown,
    Omit<CreateBlacklistBankAccountRequest, "token">,
    unknown
  >
) {
  const { token = "" } = useRecoilValue(authState);
  const queryClient = useQueryClient();
  return useMutation((request) => CreateBlacklistBankAccount({ token, ...request }), {
    onError: () => toast.error("เพิ่มชื่อบัญชีแบล็คลิสไม่สำเร็จ"),
    onSuccess: () => {
      toast.success("เพิ่มชื่อบัญชีแบล็คลิสสำเร็จ");
      queryClient.invalidateQueries([BLACKLIST_BANK_ACCOUNTS_QUERY_KEY]);
    },
    ...options,
  });
}

export type { BlacklistItem };
